import './PricingSection.scss';

import React, { memo, useRef, useState } from 'react';

import {
  Box,
  BulletList,
  BulletListContent,
  Button,
  className,
  Column,
  Divider,
  FlexBox,
  GridContainer,
  H2,
  Hidden,
  Icon,
  ListItem,
  P,
  Popover,
  PopoverContent,
  PopoverLauncher,
  Row,
  ToggleSwitch,
  ToggleSwitchOffLabel,
  ToggleSwitchOnLabel,
  Typography,
  Visible,
} from '@vp/swan';

import {
  FeatureGroupModel,
  HeroGroupModel,
  PackageCardModel,
} from '@vp/digital-common-data-model-lib';
import { isProduction } from '@vp/digital-environment-lib';
import { PageAnalyticsData } from '@vp/digital-tracking-lib';

import {
  trackPlansSectionFindOutMoreClick,
  trackPlansSectionViewMorePlanDetailsClick,
} from '../WixWebsitesAnalytics';
import MissingContentAlert from '../../common/MissingContentAlert';
import { isEnabledPlan } from '../../../helpers/packageTierMapper';
import { SubscriptionOptionsEnum } from '../../../helpers/WixPageAnalyticsProvider';
import WixNewPackageGridHeader from '../../common/wixNewPackageGrid/WixNewPackageGridHeader';
import PaymentsControlContainer from '../../common/newPaymentsControlContainer/PaymentsControlContainer';

interface PricingSectionProps {
  content?: HeroGroupModel;
  packageGridPlans: any;
  locale: string;
  productVersion: any;
  handleGetStartedNavigation: Function;
  handleWixPlansPage: Function;
  trackToggleClickedEvent: Function;
  pageAnalyticsData: PageAnalyticsData;
  accessibility: FeatureGroupModel;
}

const PricingSection = ({
  content,
  packageGridPlans,
  locale,
  productVersion,
  handleGetStartedNavigation,
  handleWixPlansPage,
  trackToggleClickedEvent,
  pageAnalyticsData,
  accessibility,
}: PricingSectionProps) => {
  const [isBilledYearly, setIsBilledYearly] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const packages = useRef(
    packageGridPlans.packages
      .filter((plan: PackageCardModel) =>
        isEnabledPlan(plan.key, locale.toLowerCase())
      )
      .splice(0, 4)
  );

  if (!content && !packageGridPlans && content.heros.length < 3) {
    return <MissingContentAlert />;
  }

  // get override params for testing
  let isOverrideSales: boolean;
  if (
    !isProduction() &&
    typeof window !== `undefined` &&
    window.location?.search
  ) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    isOverrideSales = urlParams?.get('sales') === 'true' && isBilledYearly;
  }

  const plansCtasIndex = packageGridPlans.packages.map(
    (plan: PackageCardModel) => (plan.key === 'Free' ? 2 : 3)
  );

  const onPackageSelected = (plan: PackageCardModel) => {
    if (plan.key === 'Free') {
      handleGetStartedNavigation();
    } else {
      trackPlansSectionFindOutMoreClick(plan.key);
      handleWixPlansPage();
    }
  };

  const ViewMorePlanDetails = memo(() => (
    <Button
      mt={6}
      skin="secondary"
      onClick={() => {
        trackPlansSectionViewMorePlanDetailsClick();
        handleWixPlansPage();
      }}
    >
      {content.heros[2].actions[1].title}
    </Button>
  ));

  const toggleBillingCycle = () => {
    setIsBilledYearly(!isBilledYearly);
    trackToggleClickedEvent(
      pageAnalyticsData,
      isBilledYearly
        ? SubscriptionOptionsEnum.MONTHLY
        : SubscriptionOptionsEnum.ANNUALLY
    );
  };
  return (
    <GridContainer>
      <Row>
        <Column span={12}>
          <H2 m={0} fontSize="x2large">
            {content.heros[0].title}
          </H2>
        </Column>

        <Column span={7}>
          <P>{content.heros[0].descriptionLong}</P>
        </Column>

        <Column span={5} mt={5}>
          <BillingCycleToggle
            content={content}
            isBilledYearly={isBilledYearly}
            toggleBillingCycle={toggleBillingCycle}
            accessibility={accessibility}
          />
        </Column>
      </Row>

      {/* plans header */}
      <Row mt={5}>
        <Column span={12}>
          <WixNewPackageGridHeader
            plansCtasIndex={plansCtasIndex}
            packageGridPlans={{
              ...packageGridPlans,
              packages: packages.current,
            }}
            productVersion={productVersion}
            highlightedPlans={['Business']}
            isBilledYearly={isBilledYearly}
            selectPackageCallback={onPackageSelected}
            overrideSales={isOverrideSales}
            defaultSkin="secondary"
            className={className({
              'pricing-section__headers-container__minimized':
                !isExpanded && !false,
            })}
            accessibility={{
              translations: accessibility,
            }}
          />
        </Column>
      </Row>

      {/* payments methods */}
      <Row>
        <Column span={12} textAlign={'center'}>
          <Hidden xs>
            <FlexBox alignItems="center" flexDirection="column">
              <Button
                skin="tertiary"
                iconPosition="right"
                aria-label={
                  isExpanded
                    ? content.heros[2].actions[0].hoverText
                    : content.heros[2].actions[0].valueText
                }
                aria-expanded={isExpanded}
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
              >
                {isExpanded
                  ? content.heros[2].actions[0].hoverText
                  : content.heros[2].actions[0].valueText}

                <Icon iconType={isExpanded ? 'arrowUp' : 'arrowDown'} />
              </Button>
            </FlexBox>
          </Hidden>
        </Column>
      </Row>

      <Row>
        <Column span={12} textAlign="center">
          <ViewMorePlanDetails />
        </Column>
      </Row>

      <Row
        className={className({
          'pricing-section__payment-container': false || isExpanded,
        })}
      >
        <Column span={12} mt={{ xs: 6, sm: 7 }}>
          <PaymentsControlContainer
            content={content.heros[2].heroFeatureGroup}
            isPlanPage={false}
          />
        </Column>
      </Row>
    </GridContainer>
  );
};

interface BillingCycleToggleProps {
  content?: HeroGroupModel;
  isBilledYearly: boolean;
  toggleBillingCycle: Function;
  accessibility: FeatureGroupModel;
}

const BillingCycleToggle = ({
  content,
  isBilledYearly,
  toggleBillingCycle,
  accessibility,
}: BillingCycleToggleProps) => {
  const popoverContentId = 'yearly-savings-popover-content';

  return (
    <>
      <FlexBox
        gap="5"
        alignItems="center"
        flexDirection="row"
        className="wix-new-plans-page__billing-toggle__container"
      >
        <ToggleSwitch
          mr={0}
          activated={isBilledYearly}
          onRequestActivatedChange={() => toggleBillingCycle()}
          aria-describedby="yearly-savings"
        >
          <ToggleSwitchOnLabel aria-hidden={isBilledYearly ? 'false' : 'true'}>
            {content.heros[1].title}
          </ToggleSwitchOnLabel>

          <ToggleSwitchOffLabel
            textColor="subtle"
            aria-hidden={isBilledYearly ? 'true' : 'false'}
          >
            {content.heros[1].title}
          </ToggleSwitchOffLabel>
        </ToggleSwitch>

        <Visible xs style={{ flexGrow: 1 }}>
          <Divider />
        </Visible>
      </FlexBox>

      <Box
        mt={3}
        textColor="promo"
        fontSkin="footnote-bold"
        className="wix-new-plans-page__billing-disclaimer__container"
      >
        {isBilledYearly && (
          <div id="yearly-savings">{content.heros[1].description}</div>
        )}

        {!isBilledYearly && (
          <Popover className="wix-new-plans-page__billing-toggle__popover">
            <PopoverLauncher>
              <Button
                aria-expanded="false"
                aria-controls={popoverContentId}
                id="yearly-savings"
                skin="unstyled"
              >
                <Typography
                  textColor="standard"
                  fontSize="xsmall"
                  component="span"
                  fontWeight="bold"
                >
                  {content.heros[1].descriptionLong}{' '}
                  <Typography
                    component="span"
                    textColor="error"
                    className="wix-new-plans-page__billing-toggle__description__error"
                  >
                    {content.heros[1].footer}
                  </Typography>
                </Typography>
              </Button>
            </PopoverLauncher>

            <PopoverContent
              id={popoverContentId}
              className="wix-new-plans-page__billing-toggle__popover__content"
            >
              <P fontSize="small" m={0}>
                {content.heros[1].heroFeatureGroup.title}
              </P>

              <P fontSize="small" m={0} fontWeight="bold">
                {content.heros[1].heroFeatureGroup.description}
              </P>

              <BulletList mt="5">
                {content.heros[1].heroFeatureGroup.features.map(
                  (feature: any) => (
                    <ListItem key={feature.title} p={0} mb="2">
                      <FlexBox alignItems="center">
                        <Icon iconType="error" size="16p" mr="3" />
                        <BulletListContent
                          component="p"
                          className="text-size-6"
                          fontSize="small"
                          m={0}
                        >
                          {feature.title}
                        </BulletListContent>
                      </FlexBox>
                    </ListItem>
                  )
                )}
              </BulletList>
            </PopoverContent>
          </Popover>
        )}
      </Box>
    </>
  );
};

export default PricingSection;
